// @owners { team: patients-team }
import { COLORS } from '@alto/design-library-tokens';
import {
  ActionSheetContext,
  Button,
  Card,
  type DateData,
  NavBarLogo,
  SecondaryPage,
  XsSpacing,
} from '@alto/design-system';
import { useNavigation } from '@alto/navigation';
import { differenceInDays, format, isValid, parseISO } from 'date-fns';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { updateCycleAttributes } from '~shared/actions/ui/progynyWizard';
import progynyLogo from '~shared/assets/images/progyny_by_alto_logo.png';
import getLoading from '~shared/features/ui/selectors/getLoading';
import { DATE_FORMATS } from '~shared/helpers/date';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import {
  getProgynyWizardCycle,
  getProgynyWizardFirstFill,
  getProgynyWizardUpdateAttributes,
} from '~shared/selectors/checkout/progyny';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { TRIGGER_DATE_STRINGS } from '../constants';
import { getProgynyWizardNextRoute } from '../helpers/getProgynyWizardNextRoute';
import { DatePicker } from './DatePicker';
import { ProgynyWizardStepsHeaderContent } from './ProgynyWizardStepsHeaderContent';
import { TriggerDateInfoActionSheet } from './TriggerDateInfoActionSheet';

export const TriggerDate = () => {
  const cycle = useSelectorShared(getProgynyWizardCycle);
  const updateAttributes = useSelectorShared(getProgynyWizardUpdateAttributes);
  const originalTriggerDate = cycle?.trigger_date ?? '';
  const lastSubmittedTriggerDate = updateAttributes?.trigger_date;
  const initialTriggerDate = lastSubmittedTriggerDate ?? originalTriggerDate;

  const { setActiveActionSheet } = useContext(ActionSheetContext);
  const dispatch = useDispatchShared();
  const { updateFertilityCycleLoading } = useSelectorShared(getLoading);
  const { getCurrentRouteName, navigate, goBack } = useNavigation();
  const currentRoute = getCurrentRouteName();
  const { trackPageView } = useAnalytics();
  const nextRoute = getProgynyWizardNextRoute(currentRoute);
  const isRefill = !useSelectorShared(getProgynyWizardFirstFill);
  const [triggerDate, setTriggerDate] = useState(initialTriggerDate);

  const onTriggerDateInfo = useCallback(() => {
    setActiveActionSheet(<TriggerDateInfoActionSheet />);
  }, [setActiveActionSheet]);

  const onSubmit = useCallback(
    (trigger_date: string | null | undefined) => {
      dispatch(updateCycleAttributes({ trigger_date }));
      if (nextRoute) navigate(nextRoute);
    },
    [dispatch, navigate, nextRoute],
  );

  const onSkip = useCallback(() => {
    if (nextRoute) navigate(nextRoute);
  }, [navigate, nextRoute]);

  useEffect(() => {
    trackPageView({
      event: EVENTS.TRIGGER_DATE_VIEWED,
      params: { is_refill: isRefill },
    });
  }, [isRefill, trackPageView]);

  let headline = TRIGGER_DATE_STRINGS.HEADLINE_UNKNOWN_DATE;
  let subHeadline = TRIGGER_DATE_STRINGS.SUBHEADLINE_UNKNOWN_DATE;

  if (originalTriggerDate) {
    const parsedOriginalTriggerDate = parseISO(originalTriggerDate);

    if (isValid(parsedOriginalTriggerDate)) {
      const formattedDate = format(parsedOriginalTriggerDate, DATE_FORMATS.FULL_MONTH_ORDINAL_DAY);

      if (differenceInDays(parsedOriginalTriggerDate, new Date()) > 0) {
        headline = TRIGGER_DATE_STRINGS.HEADLINE_UPCOMING_DATE(formattedDate);
        subHeadline = TRIGGER_DATE_STRINGS.SUBHEADLINE_UPCOMING_DATE;
      } else {
        headline = TRIGGER_DATE_STRINGS.HEADLINE_PAST_DATE(formattedDate);
        subHeadline = TRIGGER_DATE_STRINGS.SUBHEADLINE_PAST_DATE;
      }
    }
  }

  const noValue = !triggerDate;
  const noChanges = triggerDate === initialTriggerDate;

  const handleSave = useCallback(() => {
    onSubmit(triggerDate);
  }, [onSubmit, triggerDate]);

  const handleDayPress = useCallback((day: DateData) => {
    setTriggerDate(day.dateString);
  }, []);

  const handleClearDate = useCallback(() => {
    setTriggerDate('');
  }, []);

  return (
    <SecondaryPage
      dismissIcon="chevronleft"
      onDismiss={goBack}
      headerBackgroundColor={COLORS.BACKGROUND_COLORS.PINK_LIGHTEST}
      NavBarCenterContent={<NavBarLogo source={progynyLogo} />}
      HeaderContent={
        <ProgynyWizardStepsHeaderContent
          headline={headline}
          subheadline={subHeadline}
          linkCopy={TRIGGER_DATE_STRINGS.MORE_INFO_LABEL}
          linkOnPress={onTriggerDateInfo}
        />
      }
      buttons={[
        noChanges ? (
          <Button
            label="Continue"
            onPress={onSkip}
            disabled={noValue}
            loading={updateFertilityCycleLoading}
            testID="e2e-progyny-trigger-date-continue-button"
          />
        ) : (
          <Button
            label="Continue"
            onPress={handleSave}
            disabled={noValue}
            loading={updateFertilityCycleLoading}
            testID="e2e-progyny-trigger-date-continue-button"
          />
        ),
      ]}
    >
      <Card>
        <DatePicker
          current={triggerDate}
          markedDates={triggerDate ? { [triggerDate]: { selected: true } } : undefined}
          onDayPress={handleDayPress}
        />
      </Card>
      {!!triggerDate && (
        <>
          <XsSpacing />
          <Button
            label={TRIGGER_DATE_STRINGS.CLEAR_DATE_LABEL}
            onPress={handleClearDate}
            type="tertiary"
          />
        </>
      )}
    </SecondaryPage>
  );
};
